import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Upload, Loader2, Trash2 } from 'lucide-react';
import Button from 'components/Button';
import Toast from 'components/Toast';
import {
  deleteBoardBackground,
  getBoardBackgroundDownloadUrl,
  getBoardBackgroundUploadUrl,
  patchBoardBackground,
} from 'services/kanban';
import styles from './BoardBackgroundModal.module.scss';

interface BoardBackgroundModalProps {
  show: boolean;
  onHide: () => void;
  boardId: string;
  companyId: string | null;
  hasBackground: boolean;
  onBackgroundUpdated: (payload: {
    downloadUrl: string | null;
    backgroundImagePath: string | null;
  }) => void;
}

export default function BoardBackgroundModal({
  show,
  onHide,
  boardId,
  companyId,
  hasBackground,
  onBackgroundUpdated,
}: BoardBackgroundModalProps) {
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [existingUrl, setExistingUrl] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const showToast = useCallback(
    (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
    },
    [],
  );

  useEffect(() => {
    if (!show || !hasBackground || !boardId || !companyId) {
      setExistingUrl(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoadingExisting(true);
      try {
        const res = await getBoardBackgroundDownloadUrl(boardId, companyId);
        if (!cancelled) {
          setExistingUrl(res.downloadUrl ?? null);
        }
      } catch {
        if (!cancelled) {
          setExistingUrl(null);
        }
      } finally {
        if (!cancelled) {
          setLoadingExisting(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [show, hasBackground, boardId, companyId]);

  const handleUploadChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        showToast('Please select an image file.', 'error');
        return;
      }
      setUploading(true);
      try {
        if (!companyId) {
          throw new Error('Select a company before uploading a background.');
        }
        const { uploadUrl, path } = await getBoardBackgroundUploadUrl(
          boardId,
          {
            filename: file.name,
            contentType: file.type || 'image/jpeg',
          },
          companyId,
        );
        const putRes = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'image/jpeg' },
        });
        if (!putRes.ok) {
          throw new Error('Upload failed.');
        }
        const res = await patchBoardBackground(
          boardId,
          {
            path,
            filename: file.name,
            size: file.size,
            mimeType: file.type || 'image/jpeg',
          },
          companyId,
        );
        const download = await getBoardBackgroundDownloadUrl(
          boardId,
          companyId,
        );
        onBackgroundUpdated({
          downloadUrl: download.downloadUrl ?? null,
          backgroundImagePath: res.backgroundImagePath ?? null,
        });
        showToast('Board background updated.', 'success');
        onHide();
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Failed to upload background.';
        showToast(msg, 'error');
      } finally {
        setUploading(false);
      }
    },
    [boardId, companyId, onBackgroundUpdated, onHide, showToast],
  );

  const handleRemove = useCallback(async () => {
    if (!companyId) return;
    setRemoving(true);
    try {
      await deleteBoardBackground(boardId, companyId);
      onBackgroundUpdated({
        downloadUrl: null,
        backgroundImagePath: null,
      });
      setExistingUrl(null);
      showToast('Board background removed.', 'success');
      onHide();
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to remove background.';
      showToast(msg, 'error');
    } finally {
      setRemoving(false);
    }
  }, [boardId, companyId, onBackgroundUpdated, onHide, showToast]);

  return (
    <>
      <Modal show={show} onHide={onHide} centered>
        <Modal.Header closeButton>
          <Modal.Title>Change board background</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className={styles.uploadRow}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => void handleUploadChange(e)}
              aria-hidden
              className={styles.fileInput}
            />
            <Button
              variant="primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || removing}
            >
              {uploading ? (
                <>
                  <Loader2 size={16} className={styles.spinner} />
                  Uploading…
                </>
              ) : (
                <>
                  <Upload size={16} style={{ marginRight: 8 }} />
                  Upload background
                </>
              )}
            </Button>
          </div>
          {hasBackground && (
            <div className={styles.currentRow}>
              {loadingExisting ? (
                <p className={styles.message}>Loading current background…</p>
              ) : existingUrl ? (
                <div className={styles.currentPreview}>
                  <div
                    className={styles.currentImage}
                    style={{ backgroundImage: `url(${existingUrl})` }}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void handleRemove()}
                    disabled={removing}
                  >
                    <Trash2 size={14} style={{ marginRight: 6 }} />
                    {removing ? 'Removing…' : 'Remove background'}
                  </Button>
                </div>
              ) : (
                <p className={styles.message}>This board has a background.</p>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
}
